import {
  BarChart, Bar, LineChart, Line,
  XAxis, YAxis, ResponsiveContainer, Tooltip, LabelList,
} from 'recharts';
import { KpiCard, ChartMonth } from './mockData';

// Focus+context tile (Ohad 2026-07-02): previous months as a small line+dots trend on the
// LEFT, the current month as 3 spaced bars (יעד / תחזית / actual) on the RIGHT. The forecast
// comes from the meetings model (done + scheduled_forward × prev-month realization) — passed
// in on the current month's `forecast`. Reused for revenue (₪), total meetings, psy intakes.

const GRAY = '#94A3B8';
const GREEN = '#10B981';
const BLUE = '#2563EB';
const GHOST = 'rgba(148,163,184,0.4)';   // unify mode: the "coordinated" remainder above בוצע

const compactMoney = (n: number) => (n >= 1000 ? `₪${Math.round(n / 1000)}K` : `₪${Math.round(n)}`);

interface Props {
  kpi: KpiCard;
  data: ChartMonth[];          // trailing months; last = current (target/forecast/actual)
  onDrill: (to?: string) => void;
  fmt: (n: number) => string;  // full formatter (tooltip + headline)
  compact?: (n: number) => string;  // short label formatter (default = ₪K)
  actualName?: string;         // legend/series label for the actual series (default בפועל)
  // Ohad 2026-07-07: merge תחזית+בפועל into ONE "תואם" bar (the coordinated max = done +
  // scheduled_forward) with בוצע filling into it — same model as the staff execution bar.
  // Drops the discounted forecast; uses ChartMonth.coordinated. Current month only.
  unify?: boolean;
  accent?: string;   // color for the actual series (default green); e.g. red for no-show
}

export default function ForecastSplitTile({
  kpi, data, onDrill, fmt, compact = compactMoney, actualName = 'בפועל', unify = false, accent = GREEN,
}: Props) {
  const clickable = !!kpi.drillTo;
  const handleClick = () => { if (clickable) onDrill(kpi.drillTo); };

  const past = data.slice(0, -1);
  const cur = data[data.length - 1];
  const hasTarget = data.some((d) => (d.target ?? 0) > 0);   // no plan target (e.g. active patients) → hide יעד
  const coordVal = cur ? (cur.coordinated ?? cur.actual ?? 0) : 0;   // תואם = done + scheduled_forward
  const doneVal = cur ? (cur.actual ?? 0) : 0;                       // בוצע
  const curData = cur
    ? (unify
        ? [{ label: cur.label, target: cur.target, doneSeg: doneVal, remainderSeg: Math.max(0, coordVal - doneVal), coordinated: coordVal }]
        : [{ label: cur.label, target: cur.target, forecast: cur.forecast, actual: cur.actual }])
    : [];

  // Headline % is DONE vs target (Ohad 2026-07-20) — what's actually completed,
  // not the coordinated figure. תואם stays in the column/bar only.
  const pct = cur && cur.target ? Math.round(((cur.actual ?? 0) / cur.target) * 100) : null;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dotLabel = (props: any) => {
    const { x, y, value } = props;
    if (value == null) return null;
    return (
      <text x={x} y={y - 6} textAnchor="middle" fill={GRAY} fontSize={10.5} fontWeight={700}>
        {compact(value)}
      </text>
    );
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const barLabel = (color: string) => (props: any) => {
    const { x, y, width, value } = props;
    if (value == null) return null;
    return (
      <text x={x + width / 2} y={y - 4} textAnchor="middle" fill={color} fontSize={11} fontWeight={700}>
        {compact(value)}
      </text>
    );
  };
  // unify: בוצע label centered inside the green segment.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const segLabel = (color: string) => (props: any) => {
    const { x, y, width, height, value } = props;
    if (value == null || value === 0 || height < 12) return null;
    return (
      <text x={x + width / 2} y={y + height / 2} textAnchor="middle" dominantBaseline="central" fill={color} fontSize={11} fontWeight={700}>
        {compact(value)}
      </text>
    );
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    return (
      <div style={{ background: '#101D35', border: '1px solid #243457', borderRadius: 6, fontSize: 12, color: '#F1F5F9', direction: 'rtl', padding: '8px 10px', lineHeight: 1.5 }}>
        <div style={{ fontWeight: 600, marginBottom: 2 }}>{label}</div>
        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
        {payload.map((p: any) => (
          <div key={p.dataKey} style={{ color: p.color }}>{p.name}: {fmt(p.value)}</div>
        ))}
      </div>
    );
  };

  return (
    <div
      className={`exec-kpi exec-kpi-chart topic-${kpi.topic}${clickable ? ' clickable' : ''}`}
      onClick={handleClick}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter') handleClick(); } : undefined}
    >
      <div className="exec-kpi-chart-body">
        <div className="exec-kpi-chart-head">
          <span className="exec-kpi-label">{kpi.label}</span>
          {cur && (
            <span className="exec-kpi-chart-headline">
              <span className="exec-kpi-chart-value" style={accent !== GREEN ? { color: accent } : undefined}>{fmt(cur.actual ?? 0)}</span>
              <span className="exec-kpi-chart-meta">{cur.label}{hasTarget ? ` · ${pct ?? 0}% מהיעד` : ''}</span>
            </span>
          )}
        </div>
        <div className="exec-kpi-chart-canvas">
          <div style={{ display: 'flex', gap: 10, height: '100%' }}>
            {/* FOCUS — current month, 3 spaced bars (right side) */}
            <div style={{ flex: '0 0 40%', minWidth: 0, height: '100%' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={curData} barGap={8} barCategoryGap="18%" margin={{ top: 18, right: 4, left: 4, bottom: 0 }}>
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748B', fontWeight: 700 }} axisLine={false} tickLine={false} height={20} />
                  <Tooltip cursor={{ fill: 'rgba(148,163,184,0.08)' }} content={tip} />
                  {hasTarget && (
                    <Bar dataKey="target" name="יעד" fill={GRAY} radius={[3, 3, 0, 0]} isAnimationActive={false}>
                      <LabelList dataKey="target" content={barLabel(GRAY)} />
                    </Bar>
                  )}
                  {unify ? (
                    <>
                      {/* One "תואם" column: בוצע (green) stacked below the coordinated remainder (ghost).
                          Full height = תואם (coordinated max); green fill = בוצע. */}
                      <Bar dataKey="doneSeg" name={actualName} stackId="c" fill={accent} isAnimationActive={false}>
                        <LabelList dataKey="doneSeg" content={segLabel('#ffffff')} />
                      </Bar>
                      <Bar dataKey="remainderSeg" name="תואם" stackId="c" fill={GHOST} radius={[3, 3, 0, 0]} isAnimationActive={false}>
                        <LabelList dataKey="coordinated" position="top" content={barLabel(GRAY)} />
                      </Bar>
                    </>
                  ) : (
                    <>
                      <Bar dataKey="forecast" name="תחזית" fill={BLUE} radius={[3, 3, 0, 0]} isAnimationActive={false}>
                        <LabelList dataKey="forecast" content={barLabel(BLUE)} />
                      </Bar>
                      <Bar dataKey="actual" name={actualName} fill={accent} radius={[3, 3, 0, 0]} isAnimationActive={false}>
                        <LabelList dataKey="actual" content={barLabel(accent)} />
                      </Bar>
                    </>
                  )}
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div style={{ width: 1, background: 'rgba(148,163,184,0.2)' }} />
            {/* CONTEXT — previous months trend (left side) */}
            <div style={{ flex: '1 1 55%', minWidth: 0, height: '100%' }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={past} margin={{ top: 18, right: 10, left: 10, bottom: 0 }}>
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#64748B' }} axisLine={false} tickLine={false} interval={0} height={20} />
                  <YAxis hide domain={['auto', 'auto']} />
                  <Tooltip cursor={{ stroke: 'rgba(148,163,184,0.25)', strokeWidth: 1 }} content={tip} />
                  <Line type="monotone" dataKey="actual" name={actualName} stroke={accent} strokeWidth={2} dot={{ r: 3, fill: accent }} activeDot={{ r: 5 }} isAnimationActive={false} connectNulls={false}>
                    <LabelList dataKey="actual" content={dotLabel} />
                  </Line>
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
        <div className="exec-kpi-chart-legend">
          {hasTarget && <LegendDot color={GRAY} label="יעד" />}
          {unify ? (
            <>
              <LegendDot color={GHOST} label="תואם" />
              <LegendDot color={accent} label={actualName} />
            </>
          ) : (
            <>
              <LegendDot color={BLUE} label="תחזית" />
              <LegendDot color={accent} label={actualName} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span className="exec-kpi-chart-legend-item">
      <span className="exec-kpi-chart-legend-dot" style={{ background: color }} />
      {label}
    </span>
  );
}
